import { Lissajous3 } from "@components/Lissajous";
import Button from "components/library/Button";
import {
  ActiveLearningLogo,
  ActiveTestingLogo,
  AnnotationTeamsLogo,
  AutoTrainLogo,
  DataDebuggerLogo,
  HumanInTheLoopDeploymentLogo,
  InferenceLogo,
  MonitoringLogo,
  ProgrammaticLogo,
} from "components/ProductsLogos";
import { SectionBadge } from "components/SectionBadge";
import { useBreakpoints } from "hooks/useBreakpoints";
import { HUMANLOOP_DOCS_LINK } from "utils/constants";
import {
  ArrowUpRightIcon,
  CheckCircleIcon,
  ShapeCubeIcon,
} from "../library/Icons";
import { CTASmall } from "./CTASmall";
import { FeatureCard } from "./FeatureCard";
import { Quote } from "./Quote";
import { SectionTitleAndDescription } from "./SectionTitleAndDescription";

export const ImproveSection = () => {
  const { isMediumWidth } = useBreakpoints();

  return (
    <div className="bg-gray-100 dark:bg-black">
      <section className="container-xl">
        <div className=" relative py-48 md:pt-160">
          <div className="mx-12 mb-64 md:mx-[64px]">
            <div className="mb-12 -ml-4 md:mb-20 ">
              <SectionBadge>
                <Lissajous3 delay={90} />
                Improve
              </SectionBadge>
            </div>
            <SectionTitleAndDescription
              title="Models that continuously improve over time"
              description={
                <>
                  Your data evolves and your model should too. Our tools make it
                  easy to find issues before your users do and be confident your
                  model will perform.
                </>
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-16">
            <FeatureCard
              wide
              title={<ActiveTestingLogo size={isMediumWidth ? 24 : 16} />}
              description={
                "To enable you to trust your models, Active Testing gives you meaningful estimates of model performance from small amounts of labeled data."
              }
              callouts={[
                {
                  title: "Confidence in your evaluation",
                  description:
                    "Confidence intervals enable you to know eactly how much to trust a model.",
                },
                {
                  title: "Works with rare classes",
                  description:
                    "Automatically considers rare classes with the minimum of manual annotation.",
                },
              ]}
              learnMore={
                <Button
                  href={"/blog"}
                  styling="ghost"
                  className="-ml-12"
                  IconRight={ArrowUpRightIcon}
                >
                  Read our research
                </Button>
              }
              image={
                <div className="mx-16 ">
                  <img
                    className=" mx-auto w-4/5 rounded-ms border border-gray-400 shadow-b dark:border-none dark:shadow-none md:mt-20"
                    src="/assets/product/model-graph.svg"
                    alt="Model improvement graph with confidence bounds"
                  />
                </div>
              }
            />
            <FeatureCard
              title={<DataDebuggerLogo size={isMediumWidth ? 24 : 16} />}
              description={
                "Our data debugger finds likely misannotations and areas of your dataset where your model is weak."
              }
              callouts={[
                {
                  title: "Improve your models by improving your data",
                  description:
                    "More impactful than attempting to improve the model",
                },
              ]}
              image={
                <img
                  className="mx-auto max-h-[240px] w-4/5  rounded-ms text-left md:mx-0 md:h-[180px]"
                  src="/assets/product/data-debugger.svg"
                  alt="Data debugger"
                />
              }
            />
            <FeatureCard
              title={<MonitoringLogo size={isMediumWidth ? 24 : 16} />}
              description={"Be alerted when your model is performing poorly"}
              callouts={[
                {
                  title: "Worry-free deployment",
                  description: "Always online with clever reporting",
                },
              ]}
              image={
                <img
                  className="mx-auto max-h-[200px] w-full rounded-ms border border-gray-300 md:mx-0 md:h-[173px]"
                  src="/assets/product/data-collection-monitoring-light.svg"
                  alt="Model monitoring"
                />
              }
            />
          </div>
          <div className="my-[104px] grid grid-cols-1 gap-y-48 md:grid-cols-2">
            <CTASmall text="Improve your datasets and improve your models now" />
            <div className="md:pl-32">
              <Quote
                quote="Humanloop makes setting up complex text classification models structured and straightforward."
                name="Matt Rajcok"
                company="CEO of Blastable"
                image="/assets/customers/matt-blastable.jpg"
                products={[AutoTrainLogo, HumanInTheLoopDeploymentLogo]}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
